import {
  ArrowUpDown,
  Calendar,
  CalendarClock,
  Check,
  CircleDot,
  Tag,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { CONTROL_ACTIVE_CLASS } from "@/components/ui/control-scale";
import { cn } from "@/lib/utils";
import type { IssueGroupBy } from "@/domains/pm/utils/groupAndSort";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

const OPTIONS: { value: IssueGroupBy; icon: LucideIcon }[] = [
  { value: "creation_date", icon: CalendarClock },
  { value: "discipline", icon: Tag },
  { value: "status", icon: CircleDot },
  { value: "assignee", icon: Users },
  { value: "due_date", icon: Calendar },
];

/** The sort the list falls back to — the one the button stays quiet about. */
export const DEFAULT_ISSUE_SORT: IssueGroupBy = "creation_date";

interface IssueSortControlProps {
  value: IssueGroupBy;
  onChange: (next: IssueGroupBy) => void;
}

/**
 * "Sort" control for the PM issues list — same button styling as the
 * neighbouring `IssueFilterControl`, so the two read as one family, but
 * built on the Radix `DropdownMenu` so the menu keyboard contract (arrow
 * keys, Home/End, typeahead) comes for free.
 * It still drives `groupAndSort`'s group-by dimension (the list's group
 * headers show which one is active); only the label and presentation are
 * sort-flavoured, which is what users call it.
 *
 * Always reads "Sort: {value}", so the active ordering is legible without
 * opening the menu; the tint is reserved for a non-default choice. Its width
 * is taken from the chip row to its left (the cluster is right-pinned), so a
 * change of sort never moves the search box.
 * The active option carries a right-hand ✓, the same mark the issue context
 * menu and the comments filter use for "this is the current one".
 */
export function IssueSortControl({ value, onChange }: IssueSortControlProps) {
  const { t } = useLanguage();
  const s = t.pm.issues.sortBy;

  const optionLabel = (v: IssueGroupBy): string =>
    v === "creation_date"
      ? s.creationDate
      : v === "discipline"
        ? s.discipline
        : v === "status"
          ? s.status
          : v === "assignee"
            ? s.assignee
            : s.dueDate;

  // The button always NAMES the active sort, including the default. Reading
  // just "Sort" left the one question the control exists to answer — what is
  // this list ordered by? — answerable only by opening it.
  //
  // The tint still marks a non-default choice. That is a different signal
  // (you have changed something from the norm) and tinting always would spend
  // it: the whole toolbar would sit permanently active-coloured.
  const isDefault = value === DEFAULT_ISSUE_SORT;
  const label = `${s.title}: ${optionLabel(value)}`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          {...pageInfoTarget("issues.sort")}
          variant="outline"
          size="control"
          aria-label={label}
          className={cn("gap-1.5", !isDefault && CONTROL_ACTIVE_CLASS)}
        >
          <ArrowUpDown className="opacity-60" />
          <span className="whitespace-nowrap">{label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        {OPTIONS.map(({ value: v, icon: Icon }) => (
          // Radio semantics by hand: the shared RadioItem paints its mark as a
          // dot on the LEFT, and the app marks "the current one" with a ✓ on
          // the right (issue context menu, comments filter). Radix spreads
          // these props over its own role, so the menu still announces a
          // radio group while wearing the house indicator.
          <DropdownMenuItem
            key={v}
            role="menuitemradio"
            aria-checked={v === value}
            onSelect={() => onChange(v)}
            className="text-xs"
          >
            <span className="flex items-center gap-2">
              <Icon className="h-3.5 w-3.5 text-muted-foreground" />
              {optionLabel(v)}
            </span>
            {v === value && <Check className="ml-auto h-3.5 w-3.5" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
